import { useId, type ComponentPropsWithoutRef, type JSX } from 'react';

import { cx } from './internal/cx.js';

export type SpinnerProps = Omit<ComponentPropsWithoutRef<'span'>, 'children'> & {
  /** Accessible label announced to assistive tech when there is no visible `message`. */
  label?: string;
  /** Optional visible loading copy shown beside the spinner. */
  message?: string;
};

/** Loading indicator for explicit async UI states. */
export function Spinner({ className, label, message, ...props }: SpinnerProps): JSX.Element {
  const messageId = useId();
  const hasMessage = Boolean(message);
  // Prefer visible message as the accessible name to avoid double-announcing
  // aria-label + the same text. Explicit `label` still wins when provided.
  const ariaLabel = label ?? (hasMessage ? undefined : 'Loading');
  const ariaLabelledBy = !label && hasMessage ? messageId : undefined;

  return (
    <span
      className={cx('spinner', className)}
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      {...props}
    >
      <span className="spinner__glyph" aria-hidden="true" />
      {message ? (
        <span
          id={messageId}
          className="spinner__message"
          // When an explicit label overrides AT naming, hide the visual copy
          // from assistive tech so it is not announced twice.
          aria-hidden={label ? true : undefined}
        >
          {message}
        </span>
      ) : null}
    </span>
  );
}
